import api from '@/api';
import isEmpty from 'lodash/isEmpty';

import { prepareResearch, filterMultiResponse } from './normalize';

const requestType = {
    SINGLE: 'single',
    MULTIPLE: 'multiple',
};

/*
    Содержит информацию по данным РИГИС остаточной нефтенасыщенности.
*/

/*
    Описание:
    Данные этой модели используются как в отдельном окне, так и в скважинах.
    Скважина должна запрашивать данные только тогда, когда колонка видна (вне зависимости, группа или отдельная скважина).
    Модальное окно запрашивает данные у всех видимых в данный момент скважин.

    * initByWell - данные по одной скважине
    * init - данные модального окна

    Данные пере-используются обеими сущностями. Разница в точке их получения.

    TODO: код потенциально можно улучшить
*/

/*
    Ошибки обрабатываются, но игнорируются
*/
export const rigisResidueModel = {
    name: 'rigisResidue',
    state: {
        // TODO: Переименовать!
        rigisResidue: {},
        /*
            Словарь видимости исследований для каждой скважины
        */
        researches: {},
        /*
            Поле для ошибок, пока не используется, так как ошибки игнорируются в текущей реализации
        */
        rigisResidueErrors: {
            // [wellId]: { ERROR }
        },
        /*
            В текущей реализации ключ на индикацию загрузки - один, так как данные отображаются все вместе в одной таблице.
            Когда дело дойдет до отрисовки - надо будет изменить подход.
        */
        isLoading: false,
    },
    reducers: {
        setIsLoading(state, payload) {
            return { ...state, isLoading: payload };
        },
        /*
            Payload может быть либо объектом, либо массивом с несколькими значениями
        */
        setRigisResidue(state, payload) {
            let newRigisResidue = {};
            let newResearches = {};

            if (Array.isArray(payload)) {
                const result = payload.reduce((acc, item) => {
                    acc[item.wellId] = item.data;
                    return acc;
                }, {});

                newRigisResidue = result;
            } else {
                const { wellId, data } = payload;

                newRigisResidue = { [wellId]: data };
                newResearches = { [wellId]: data ? prepareResearch(data.children) : {} };
            }

            return {
                ...state,
                rigisResidue: {
                    ...state.rigisResidue,
                    ...newRigisResidue,
                },
                researches: {
                    ...state.researches,
                    ...newResearches,
                },
            };
        },

        setResearchesByWell(state, payload) {
            const { wellId, data } = payload;
            const { researches } = state;

            const updateResearch = { ...researches, [wellId]: data ? prepareResearch(data.children) : {} };
            return { ...state, researches: updateResearch };
        },
        changeVisible(state, payload) {
            const { wellId, stratumId } = payload;
            const { researches } = state;

            const wellResearches = researches[wellId];
            const updateWellResearches = { ...wellResearches, [stratumId]: !wellResearches[stratumId] };
            const updateResearches = { ...researches, [wellId]: updateWellResearches };

            return { ...state, researches: updateResearches };
        },
        resetResearchVisibilityById(state, wellId) {
            const { researches } = state;

            // eslint-disable-next-line no-unused-vars
            const { [wellId]: removedId, ...rest } = researches;

            return { ...state, researches: rest };
        },
    },
    effects: dispatch => ({
        async initByWell(wellId, rootState) {
            dispatch.tablets.incrementingCurrentRequests();

            const { rigisResidue } = rootState.rigisResidue;

            if (wellId in rigisResidue) {
                console.log(`%c${wellId} without request rigis residue`, 'color: green');
                this.setResearchesByWell({ wellId, data: rigisResidue[wellId] });
                dispatch.tablets.decrementingCurrentRequests();
                return;
            }

            try {
                const response = await this.getRigisResidue(wellId);
                this.setRigisResidue(response);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.log(error.message || 'getRigisResidue error');
            }

            dispatch.tablets.decrementingCurrentRequests();
        },
        /*
            Метод запускает инициализацию данных для отрисовки таблицы Rigis Residue;

            * Для одиночной скважины
            * Для группы скважин

            Помимо запросов на получение данных, происходит проверка на существование данных в текущей модели.
            Если данные присутствуют, запроса не происходит.
        */
        async init(_, rootState) {
            const { isGroup, activeTabletId, activeGroupIds } = rootState.tablets;
            const { rigisResidue } = rootState.rigisResidue;

            if (activeTabletId) {
                if (activeTabletId in rigisResidue) {
                    // Оставить для логирования
                    // eslint-disable-next-line no-console
                    console.log(`%c${activeTabletId} without request rigis residue`, 'color: green');
                    return;
                }

                try {
                    const response = await this.loadRigisResidue({ type: requestType.SINGLE, payload: activeTabletId });
                    /*
                        Почему снова массив??
                    */
                    this.setRigisResidue(response);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.log(error.message || 'getRigisResidue error');
                    this.setIsLoading(false);
                }
            }

            if (isGroup) {
                /*
                    Отбрасываем все существующие в состоянии ids.

                    Отсутствие id в состоянии может быть вызвано следующим:
                    - данные еще не запрашивали
                    - при получении данных была ошибка
                */
                const filteredExistingIDs = activeGroupIds.filter(activeID => !(activeID in rigisResidue));

                if (isEmpty(filteredExistingIDs)) return;

                const multiResponse = await this.loadRigisResidue({
                    type: requestType.MULTIPLE,
                    payload: filteredExistingIDs,
                });

                const result = filterMultiResponse(multiResponse);
                result && this.setRigisResidue(result);
            }
        },
        async getRigisResidue(wellId) {
            // Оставить для логирования
            console.log(`%c${wellId} with request::getRigisResidue`, 'color: red');

            const requestBody = { well: wellId, typeReport: 'rigisDataWithResidueTree' };

            try {
                const response = await api.carottage.fetchRigisData(requestBody);
                /*
                    Если данных нет (пустой массив), возвращается null для удобства проверок в дальнейшем;
                */
                console.log('getRigisResidue', response);
                return { wellId, data: response[0] || null };
            } catch (error) {
                // eslint-disable-next-line no-undef
                return Promise.reject(error);
            }
        },
        async getRigisResidueAll(wellIDs) {
            // eslint-disable-next-line no-undef
            const multiResponse = await Promise.allSettled(
                wellIDs.map(wellId => {
                    return this.getRigisResidue(wellId);
                }),
            );

            return multiResponse;
        },
        /*
            В зависимости от конфигурации параметра opts, метод решает, какой запрос делать.
            Так же все запросы отслеживаются флагом isLoading.

            Сделано так, чтобы методы получения данных можно было переиспользовать без связей с этой моделью.
        */
        async loadRigisResidue(opts) {
            const { type, payload } = opts;
            let response;

            this.setIsLoading(true);

            if (type === requestType.SINGLE) {
                response = await this.getRigisResidue(payload);
            }

            if (type === requestType.MULTIPLE) {
                response = await this.getRigisResidueAll(payload);
            }

            this.setIsLoading(false);
            return response;
        },
    }),
};
